import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Sparkles, MapPin, CalendarDays } from "lucide-react";
import { galleryApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Deterministic layout weights so tiles feel like a mosaic without shuffling
// on every render.
const SPAN_PATTERN = [
  { col: "md:col-span-2", row: "md:row-span-2" },
  { col: "md:col-span-1", row: "md:row-span-1" },
  { col: "md:col-span-1", row: "md:row-span-2" },
  { col: "md:col-span-2", row: "md:row-span-1" },
  { col: "md:col-span-1", row: "md:row-span-1" },
  { col: "md:col-span-1", row: "md:row-span-1" },
];

const Gallery = () => {
  const [category, setCategory] = useState("All");
  const [openIdx, setOpenIdx] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => {
    galleryApi.list().then(setAll).catch(() => setAll([]));
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(all.map((g) => g.category)))],
    [all],
  );

  const items = useMemo(() => {
    if (category === "All") return all;
    return all.filter((g) => g.category === category);
  }, [category, all]);

  const openLightbox = (idx) => setOpenIdx(idx);
  const closeLightbox = () => setOpenIdx(null);
  const prev = () =>
    setOpenIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  const next = () =>
    setOpenIdx((i) => (i === null ? null : (i + 1) % items.length));

  const active = openIdx !== null ? items[openIdx] : null;

  return (
    <div className="relative bg-white" data-testid="page-gallery">
      {/* Header */}
      <section className="relative overflow-hidden bg-apicon-mist/50 py-20 md:py-24">
        <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-60" />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-apicon-gold/15 blur-3xl" />

        <div className="relative mx-auto grid w-full max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              Gallery
            </div>
            <h1 className="mt-3 font-display text-5xl font-bold tracking-tight text-apicon-navy sm:text-6xl md:text-7xl">
              Moments from <br />
              <span className="italic text-apicon-green">Tinsukia.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
              A living archive — sessions, cultural evenings, workshops and the
              landscapes that make APICON&rsquo;s Assam edition unforgettable.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <CalendarDays className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">18 – 20 December, 2026</span>
              </span>
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <MapPin className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">
                  Tinsukia Medical College · Assam
                </span>
              </span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-3xl border border-apicon-navy/10 bg-white p-6 shadow-sm">
              <Sparkles className="h-6 w-6 text-apicon-gold" />
              <div className="mt-4 font-display text-lg font-semibold text-apicon-navy">
                Share your frames
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Have images from a previous APICON? We&rsquo;d love to include
                them here — write to{" "}
                <a
                  href="mailto:media@apicon2026.in"
                  className="font-medium text-apicon-navy underline underline-offset-4"
                >
                  media@apicon2026.in
                </a>
                .
              </p>
              <Link to="/register" className="mt-6 block" data-testid="gallery-register-cta">
                <Button className="w-full rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]">
                  Register for 2026
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="mx-auto w-full max-w-7xl px-5 pt-16 md:px-8">
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="gallery-filters"
        >
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200",
                category === c
                  ? "border-apicon-navy bg-apicon-navy text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:border-apicon-navy/40 hover:text-apicon-navy",
              )}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
            >
              {c}
              <span
                className={cn(
                  "ml-2 text-[10px] font-semibold",
                  category === c ? "text-apicon-gold" : "text-gray-400",
                )}
              >
                {c === "All"
                  ? all.length
                  : all.filter((g) => g.category === c).length}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Mosaic grid */}
      <section
        className="mx-auto w-full max-w-7xl px-5 py-10 pb-24 md:px-8"
        data-testid="gallery-grid"
      >
        <div className="grid auto-rows-[180px] grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {items.map((g, i) => {
            const span = SPAN_PATTERN[i % SPAN_PATTERN.length];
            return (
              <motion.button
                key={g.id}
                type="button"
                onClick={() => openLightbox(i)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                className={cn(
                  "group relative overflow-hidden rounded-2xl bg-apicon-mist ring-1 ring-black/5 focus:outline-none focus:ring-2 focus:ring-apicon-navy",
                  span.col,
                  span.row,
                )}
                data-testid={`gallery-item-${g.id}`}
              >
                <img
                  src={g.src}
                  alt={g.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-apicon-navy/80 via-apicon-navy/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                    {g.category}
                  </div>
                  <div className="mt-1 font-display text-base font-semibold leading-tight">
                    {g.title}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-apicon-navy/95 px-4 py-6 backdrop-blur-sm"
            data-testid="gallery-lightbox"
            onClick={closeLightbox}
          >
            {/* Top bar */}
            <div className="absolute inset-x-0 top-0 flex items-start justify-between p-5 text-white">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                  {active.category}
                </div>
                <div className="mt-1 font-display text-xl font-semibold">
                  {active.title}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/20"
                aria-label="Close"
                data-testid="gallery-lightbox-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Prev */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/20"
              aria-label="Previous"
              data-testid="gallery-lightbox-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Image */}
            <motion.img
              key={active.id}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={active.src}
              alt={active.title}
              className="max-h-[78vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/20"
              aria-label="Next"
              data-testid="gallery-lightbox-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Caption */}
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 text-white">
              <div className="max-w-2xl text-sm text-white/80">
                {active.caption}
              </div>
              <div className="text-xs text-white/60">
                {openIdx + 1} / {items.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
